import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { supabase } from "@/integrations/supabase/client";
import { listMedia, deleteMedia } from "@/lib/admin.functions";
import { toast } from "sonner";
import { Loader2, Trash2, Copy, Image as ImageIcon, Film } from "lucide-react";
import { PageHeader, FileInput } from "@/components/admin/shared";

export const Route = createFileRoute("/admin/media")({
  component: MediaPage,
});

function MediaPage() {
  const list = useServerFn(listMedia);
  const del = useServerFn(deleteMedia);
  const [bucket, setBucket] = useState<"product-images" | "product-videos">(
    "product-images",
  );
  const [items, setItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);

  const reload = async () => {
    setLoading(true);
    try {
      setItems((await list({ data: { bucket } })) as any[]);
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    reload();
  }, [bucket]);

  const upload = async (files: FileList) => {
    setUploading(true);
    try {
      for (const f of Array.from(files)) {
        const ext = f.name.split(".").pop() || "bin";
        const path = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}.${ext}`;
        const { error } = await supabase.storage
          .from(bucket)
          .upload(path, f, { contentType: f.type });
        if (error) throw error;
      }
      toast.success("Uppladdat");
      await reload();
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setUploading(false);
    }
  };

  const remove = async (name: string) => {
    if (!confirm("Ta bort filen?")) return;
    try {
      await del({ data: { bucket, name } });
      toast.success("Borttagen");
      await reload();
    } catch (e: any) {
      toast.error(e.message);
    }
  };

  return (
    <>
      <PageHeader title="Media">
        <div className="flex gap-1 rounded-full bg-secondary p-1">
          {(
            [
              ["product-images", "Bilder", ImageIcon],
              ["product-videos", "Videor", Film],
            ] as const
          ).map(([v, l, Icon]) => (
            <button
              key={v}
              onClick={() => setBucket(v as any)}
              className={`px-4 py-1.5 text-sm font-semibold rounded-full inline-flex items-center gap-1.5 ${
                bucket === v ? "bg-background shadow" : "text-muted-foreground"
              }`}
            >
              <Icon className="h-4 w-4" /> {l}
            </button>
          ))}
        </div>
        <FileInput
          accept={bucket === "product-images" ? "image/*" : "video/*"}
          multiple
          label="Ladda upp"
          onFiles={upload}
          disabled={uploading}
        />
      </PageHeader>

      {loading ? (
        <Loader2 className="h-5 w-5 animate-spin" />
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {items.map((it) => (
            <div
              key={it.name}
              className="rounded-xl overflow-hidden border border-border bg-card group"
            >
              <div className="aspect-square bg-secondary flex items-center justify-center overflow-hidden">
                {bucket === "product-images" ? (
                  <img
                    src={it.url}
                    alt={it.name}
                    loading="lazy"
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <video src={it.url} className="h-full w-full object-cover" />
                )}
              </div>
              <div className="p-2 text-xs">
                <p className="truncate font-mono">{it.name}</p>
                <div className="mt-1.5 flex gap-1">
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(it.url);
                      toast.success("URL kopierad");
                    }}
                    className="flex-1 inline-flex items-center justify-center gap-1 rounded-md bg-secondary px-2 py-1 hover:bg-secondary/70"
                  >
                    <Copy className="h-3 w-3" /> URL
                  </button>
                  <button
                    onClick={() => remove(it.name)}
                    className="p-1.5 rounded-md bg-destructive text-destructive-foreground"
                  >
                    <Trash2 className="h-3 w-3" />
                  </button>
                </div>
              </div>
            </div>
          ))}
          {items.length === 0 && (
            <p className="text-sm text-muted-foreground col-span-full">Inga filer.</p>
          )}
        </div>
      )}
    </>
  );
}
